// Content for the "Intelligence opérationnelle" dashboard section on the Requests page.
// All values are illustrative product data shown inside the dashboard mock.

export type Bi = { fr: string; en: string }
export type Status = 'red' | 'amber' | 'green'

export const intelligenceIntro = {
  eyebrow: { fr: 'Intelligence opérationnelle', en: 'Operational intelligence' },
  titleA: { fr: 'Chaque demande devient aussi', en: 'Every request also becomes' },
  titleB: { fr: 'une source de renseignements.', en: 'intelligence.' },
  subline: { fr: 'Voyez ce qui se passe. Comprenez pourquoi. Agissez.', en: 'See what is happening. Understand why. Act.' },
  body: {
    fr: 'Requêtes ATNA transforme l’activité opérationnelle en une vue en temps réel des volumes, délais, SLA, tendances et enjeux — puis recommande les actions à prendre.',
    en: 'ATNA Requests turns operational activity into a real-time view of volumes, processing times, SLAs, trends and issues — then recommends the actions to take.',
  },
}

export const sidebarItems: { key: string; label: Bi; active?: boolean }[] = [
  { key: 'dashboard', label: { fr: 'Tableau de bord', en: 'Dashboard' } },
  { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' }, active: true },
  { key: 'map', label: { fr: 'Carte', en: 'Map' } },
  { key: 'analytics', label: { fr: 'Analytique', en: 'Analytics' } },
  { key: 'atna', label: { fr: 'IA ATNA', en: 'ATNA AI' } },
  { key: 'comms', label: { fr: 'Communications', en: 'Communications' } },
  { key: 'permits', label: { fr: 'Permis', en: 'Permits' } },
  { key: 'leisure', label: { fr: 'Loisirs', en: 'Recreation' } },
  { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
  { key: 'accounts', label: { fr: 'Comptes', en: 'Accounts' } },
  { key: 'users', label: { fr: 'Utilisateurs', en: 'Users' } },
  { key: 'settings', label: { fr: 'Paramètres', en: 'Settings' } },
]

export const kpis: { key: string; value: Bi; label: Bi; note: Bi; tone: 'indigo' | Status }[] = [
  {
    key: 'received',
    value: { fr: '60 058', en: '60,058' },
    label: { fr: 'Demandes reçues', en: 'Requests received' },
    note: { fr: '+11 % par rapport au mois précédent', en: '+11% vs previous month' },
    tone: 'indigo',
  },
  {
    key: 'ontime',
    value: { fr: '81 %', en: '81%' },
    label: { fr: 'Résolues dans les délais', en: 'Resolved on time' },
    note: { fr: '+10 points', en: '+10 points' },
    tone: 'green',
  },
  {
    key: 'below',
    value: { fr: '2', en: '2' },
    label: { fr: 'Services sous la cible', en: 'Services below target' },
    note: { fr: 'Intervention requise', en: 'Action required' },
    tone: 'red',
  },
  {
    key: 'watch',
    value: { fr: '3', en: '3' },
    label: { fr: 'Services à surveiller', en: 'Services to watch' },
    note: { fr: 'Tendance à la hausse', en: 'Trending up' },
    tone: 'amber',
  },
]

// Daily request volume (30 days), values out of 2 000.
export const volumeBars = [
  1180, 1420, 1260, 1540, 1380, 1210, 1460, 1620, 1350, 1290, 1510, 1440, 1730, 1580, 1320, 1470,
  1690, 1560, 1400, 1610, 1780, 1500, 1360, 1650, 1720, 1540, 1480, 1700, 1640, 1760,
]
export const volumeAxis: Bi[] = [
  { fr: '1er sept.', en: 'Sep 1' },
  { fr: '8 sept.', en: 'Sep 8' },
  { fr: '15 sept.', en: 'Sep 15' },
  { fr: '22 sept.', en: 'Sep 22' },
  { fr: '29 sept.', en: 'Sep 29' },
]

export const servicePerformance: { service: Bi; requests: Bi; perf: number; gap: Bi; status: Status }[] = [
  { service: { fr: 'Éclairage public', en: 'Street lighting' }, requests: { fr: '1 248', en: '1,248' }, perf: 46, gap: { fr: '−34 %', en: '−34%' }, status: 'red' },
  { service: { fr: 'Signalisation', en: 'Signage' }, requests: { fr: '842', en: '842' }, perf: 71, gap: { fr: '−9 %', en: '−9%' }, status: 'amber' },
  { service: { fr: 'Collectes', en: 'Waste collection' }, requests: { fr: '688', en: '688' }, perf: 79, gap: { fr: '−1 %', en: '−1%' }, status: 'amber' },
  { service: { fr: 'Voirie', en: 'Roads' }, requests: { fr: '602', en: '602' }, perf: 86, gap: { fr: '+6 %', en: '+6%' }, status: 'green' },
  { service: { fr: 'Permis', en: 'Permits' }, requests: { fr: '514', en: '514' }, perf: 92, gap: { fr: '+12 %', en: '+12%' }, status: 'green' },
]

// Heatmap: 4 rows × 6 columns of sectors. [1][1] is Secteur A.
export const sectorGrid: Status[][] = [
  ['amber', 'red', 'amber', 'amber', 'green', 'green'],
  ['red', 'red', 'amber', 'amber', 'amber', 'green'],
  ['amber', 'red', 'amber', 'green', 'green', 'green'],
  ['green', 'amber', 'green', 'green', 'green', 'green'],
]
export const selectedSector = { row: 1, col: 1 }

export const sectorDetail = {
  name: { fr: 'Secteur A', en: 'Sector A' },
  status: { fr: 'Sous la cible', en: 'Below target' },
  requests: { fr: '412 demandes', en: '412 requests' },
  slaLabel: { fr: 'SLA global', en: 'Overall SLA' },
  sla: { fr: '46 %', en: '46%' },
  affectedTitle: { fr: 'Services les plus touchés', en: 'Most affected services' },
  affected: [
    { label: { fr: 'Éclairage public', en: 'Street lighting' }, perf: 46, status: 'red' as Status },
    { label: { fr: 'Signalisation', en: 'Signage' }, perf: 71, status: 'amber' as Status },
    { label: { fr: 'Collectes', en: 'Waste collection' }, perf: 79, status: 'amber' as Status },
  ],
}

export const recommendation = {
  heading: { fr: 'ATNA recommande', en: 'ATNA recommends' },
  pill: { fr: 'Action prioritaire', en: 'Priority action' },
  title: { fr: 'Pression anormale sur l’éclairage dans le secteur A.', en: 'Unusual pressure on street lighting in Sector A.' },
  body: {
    fr: '83 % des citoyens touchés sont joignables via les canaux numériques BCITI | ATNA. Une communication proactive pourrait réduire les appels entrants pendant la période d’intervention.',
    en: '83% of affected residents are reachable through BCITI | ATNA digital channels. Proactive communication could reduce incoming calls during the intervention period.',
  },
  actions: [
    { key: 'target', label: { fr: 'Cibler les citoyens du secteur A', en: 'Target residents of Sector A' } },
    { key: 'notify', label: { fr: 'Envoyer une notification proactive', en: 'Send a proactive notification' } },
    { key: 'schedule', label: { fr: 'Inclure l’échéancier des travaux', en: 'Include the work schedule' } },
  ],
  cta: { fr: 'Préparer la communication', en: 'Prepare the communication' },
}

export const digitalReach = {
  title: { fr: 'Portée numérique BCITI | ATNA', en: 'BCITI | ATNA digital reach' },
  sub: { fr: 'Des citoyens plus faciles à joindre, pour mieux agir.', en: 'More residents within reach, for better action.' },
  metrics: [
    { key: 'ids', value: { fr: '42 105', en: '42,105' }, label: { fr: 'Identités numériques', en: 'Digital identities' } },
    { key: 'cards', value: { fr: '38 902', en: '38,902' }, label: { fr: 'Cartes numériques BCITI actives', en: 'Active BCITI Digital Cards' } },
    { key: 'reach', value: { fr: '86 %', en: '86%' }, label: { fr: 'Joignables numériquement', en: 'Digitally reachable' } },
    { key: 'open', value: { fr: '74 %', en: '74%' }, label: { fr: 'Taux d’ouverture des notifications', en: 'Notification open rate' } },
  ],
}
